<!-- eslint-disable vue/no-v-html -->
<template>
  <span class="x-icon" :class="{ color }" v-html="svgContent" />
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue'

interface XIconProps {
  color?: string
  icon: string | string[]
  width?: number | string
  height?: number | string
}

const props = defineProps<XIconProps>()

const svgContent = ref<string | null>(null)

const computedColor = computed(() => {
  if (props.color === 'theme') {
    return 'var(--ys-color-brand-primary)'
  }
  return props.color
})

const computedDimensions = computed(
  (): { width?: string; height?: string } => ({
    width: props.width?.toString().replace(/(.*\d)$/, '$1px'),
    height: props.height?.toString().replace(/(.*\d)$/, '$1px'),
  })
)

// The webpackInclude regex will create seperate bundles for all matches
// https://webpack.js.org/api/module-methods/#magic-comments
// Takes an svg icon filename and returns promise that resolves to svg string
const loadSVG = async (icon: string): Promise<{ default: string }> =>
  await import(`../../../assets/icons/${icon.replace('.svg', '')}.svg?raw`)

const loadIcon = async (iconPaths?: string | string[], index: number = 0) => {
  if (!iconPaths) {
    return
  }

  let path = iconPaths
  const hasFallbacks = Array.isArray(iconPaths)
  if (hasFallbacks) {
    path = iconPaths[index]
  }
  try {
    const icon = await loadSVG(path as string)
    svgContent.value = icon.default
  } catch (error) {
    // There was an error loading the svg try the fallbacks
    if (hasFallbacks && index < iconPaths.length) {
      await loadIcon(iconPaths, index + 1)
    }
  }
}

watch(
  () => props.icon,
  (newIcon) => loadIcon(newIcon)
)

onBeforeMount(async () => {
  await loadIcon(props.icon)
})
</script>
<style scoped>
.x-icon :deep(svg) {
  width: v-bind('computedDimensions.width');
  height: v-bind('computedDimensions.height');
}

.x-icon.color :deep([fill]) {
  fill: v-bind(computedColor);
}
</style>
